/**
 * Assessment Favorite API Service
 * Handles job favoriting/liking functionality
 */

import http from '../client';

// Standardized response handlers
const handleApiSuccess = (response) => ({
  success: true,
  data: response.data?.data || response.data,
  status: response.status,
  message: response.data?.message || 'Success'
});

const handleApiError = (error) => ({
  success: false,
  error: error.message || 'An error occurred',
  status: error.status || 500,
  requiresAuth: error.status === 401
});

/**
 * Toggle favorite status for a job assessment
 * @param {number} assessmentId - Job assessment ID
 * @returns {Promise<Object>} { success, data: { isFavorited, assessmentId } }
 */
export const toggleFavorite = async (assessmentId) => {
  try {
    const response = await http.post('/assessment-favorite/toggle', null, {
      params: { assessmentId }
    });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Check if a job assessment is favorited by current user
 * @param {number} assessmentId - Job assessment ID
 * @returns {Promise<Object>} { success, data: { isFavorited, assessmentId } }
 */
export const checkFavorite = async (assessmentId) => {
  try {
    const response = await http.get('/assessment-favorite/check', { assessmentId });
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get list of favorited job assessments for current user
 * @returns {Promise<Object>} { success, data: Array<AssessmentFavorite> }
 */
export const getFavorites = async () => {
  try {
    const response = await http.get('/assessment-favorite/list');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

/**
 * Get count of favorites for current user
 * @returns {Promise<Object>} { success, data: { count } }
 */
export const getFavoritesCount = async () => {
  try {
    const response = await http.get('/assessment-favorite/count');
    return handleApiSuccess(response);
  } catch (error) {
    return handleApiError(error);
  }
};

const assessmentFavoriteService = {
  toggleFavorite,
  checkFavorite,
  getFavorites,
  getFavoritesCount
};

export default assessmentFavoriteService;
